import {STRETCHED_OVERLAY_CLASS, Typography} from '@sollapay/ui/components';
import {cn} from '@sollapay/ui/lib';
import {Link} from 'react-router-dom';

import {useOverflowTitle} from '@/hooks';

import {TrustAccountAvatar} from './TrustAccountAvatar';
import {TrustAccountNumber} from './TrustAccountNumber';

import type {TrustPurpose} from '@sollapay/enums';
import type {FC, ReactNode} from 'react';

/**
 * `rowLinkTo` makes the name a link stretched over its `rowLink` table row (or `group/row` card).
 * `compact` (the default) is the 34px-tile table row, `card` the grid card's tile with a two-line name.
 */
export interface TrustAccountNameCellProps {
  name: string;
  refId: string;
  trustPurpose: TrustPurpose | null | undefined;
  rowLinkTo?: string;
  variant?: 'compact' | 'card';
  roleBadge?: ReactNode;
}

const TILE_SIZE_BY_VARIANT = {compact: 'sm', card: 'lg'} as const;

const TILE_HOVER_CLASS =
  'transition-colors group-hover/row:border-brand-accent/20 group-hover/row:bg-background-selected';

export const TrustAccountNameCell: FC<TrustAccountNameCellProps> = ({
  name,
  refId,
  trustPurpose,
  rowLinkTo,
  variant = 'compact',
  roleBadge
}) => {
  const isCard = variant === 'card';
  const {onMouseEnter: showTitleIfCutOff} = useOverflowTitle<HTMLAnchorElement>(name);
  const nameClassName = cn(
    'transition-colors',
    !isCard && 'max-w-85',
    isCard && 'line-clamp-2 whitespace-normal text-pretty text-lg leading-6'
  );

  return (
    <div
      data-slot="trust-account-name-cell"
      className={cn('flex min-w-0 gap-4', isCard ? 'items-start gap-3' : 'items-center')}
    >
      <TrustAccountAvatar
        name={name}
        trustPurpose={trustPurpose ?? undefined}
        size={TILE_SIZE_BY_VARIANT[variant]}
        badge={roleBadge}
        className={TILE_HOVER_CLASS}
      />
      <div className={cn('flex min-w-0 flex-col gap-0.5', isCard && 'gap-1')}>
        {rowLinkTo ? (
          <Link
            to={rowLinkTo}
            onMouseEnter={showTitleIfCutOff}
            className={cn(
              'text-base font-semibold text-fg outline-none group-hover/row:text-brand-accent',
              !isCard && 'truncate',
              nameClassName,
              STRETCHED_OVERLAY_CLASS
            )}
          >
            {name}
          </Link>
        ) : (
          <Typography
            size="md"
            weight="semibold"
            className={cn('group-hover/row:text-brand-accent', nameClassName)}
          >
            {name}
          </Typography>
        )}
        {/* Lifted above the row link's overlay so the chip stays clickable. */}
        <div className={cn('-ms-1.5 w-fit', isCard && '-mt-0.5', rowLinkTo && 'relative z-10')}>
          <TrustAccountNumber refId={refId} variant="row" />
        </div>
      </div>
    </div>
  );
};
